// Retroactive Optimization: send one live page back through the agent queue, and keep a journal of
// every time it happened. A Keyword Density Fix is the same journey with a narrower brief: bring the chosen
// phrases into 0.5–1% of the words, cutting the ones said too often and working in a target said too rarely
// (the screen in scripts/lib/overopt.mjs finds them). Journal row kind 'density_fix', with the phrases as
// measured when queued in targets. A Templated Content Fix rewrites a page that is mostly a copy of a sibling
// (the screen in scripts/lib/sameness.mjs); kind 'template_fix', the siblings as measured in targets.
//
// The job is an ordinary fulfillment job, so the dispatcher needs no new kind. Its queue note starts
// "Retroactive Optimization" and names the retro-opt skill, which writes the spec as "## Analysis" then
// "## Plan", the build report as the Execution doc and the approval as the Verify doc. The journal row
// (page_optimizations) outlives the job: the page, the human's note, the numbers when it was queued, and
// shipped_at, which a trigger stamps when the job reaches complete.

import { supabase } from './supabase';
import { enqueueAiJob, type AiJob } from './aiJobs';
import type { Database } from './database.types';
import type { JobBrief } from './supabase.types';
import { PROMPT_DEFAULTS, loadPromptTemplate, renderPrompt } from './prompts';
import { overoptOf, templateOf, type OveroptPhrase, type PageHistoryRow, type PageRow } from '../components/clientDetail/sections/pages/format';

export type PageOptimizationRow = Database['public']['Tables']['page_optimizations']['Row'];

export type RetroOptJob = Pick<AiJob, 'id' | 'status' | 'title' | 'spec_body' | 'report_body' | 'approval_body' | 'updated_at'>;

export type PageOptimization = PageOptimizationRow & { job: RetroOptJob | null };

export interface RetroOptBaseline {
  window: string | null;
  clicks: number | null;
  impressions: number | null;
  ctr: number | null;
  position: number | null;
  queries: number | null;
}

export type RetroOptBrief = JobBrief & {
  retro_opt: {
    page_id: string;
    url: string;
    primary_phrase: string | null;
    note: string;
    baseline: RetroOptBaseline;
  };
};

export type DensityFixBrief = JobBrief & {
  density_fix: {
    page_id: string;
    url: string;
    phrases: OveroptTarget[];
    note: string;
    baseline: RetroOptBaseline;
  };
};

/** A phrase the fix should bring into line: the screen's numbers when there were any, just the words for one typed in. */
export type OveroptTarget = Pick<OveroptPhrase, 'phrase'> & Partial<Omit<OveroptPhrase, 'phrase'>>;

export const OPTIMIZATION_KIND_LABEL: Record<string, string> = {
  retro_opt: 'Retroactive Optimization',
  density_fix: 'Keyword Density Fix',
  template_fix: 'Templated Content Fix',
  links_fix: 'Internal Links Fix',
  index_request: 'Indexing requested',
};

export type EnqueueRetroOptInput = {
  client: { id: string; name: string };
  page: PageRow;
  primaryPhrase: string;
  note: string;
  createdBy: { id: string; name: string };
};

function noonTodayIso(): string {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate(), 12).toISOString();
}

const pathOf = (url: string) => {
  try {
    return new URL(url).pathname;
  } catch {
    return url;
  }
};

export function baselineOf(page: PageRow): RetroOptBaseline {
  return {
    window: page.gsc_date_from ? `${page.gsc_date_from}..${page.gsc_date_to}` : null,
    clicks: page.gsc_clicks,
    impressions: page.gsc_impressions,
    ctr: page.gsc_ctr,
    position: page.gsc_position,
    queries: page.gsc_queries,
  };
}

const num = (v: number | null, digits = 0) => (v == null ? '—' : Number(v).toFixed(digits));

/** "Search Console when queued (window): 3 clicks, 288 impressions, CTR 1.0%, avg position 26.4, 59 queries." */
function searchConsoleLine(b: RetroOptBaseline): string {
  return (
    `Search Console when queued (${b.window ?? 'no window'}): ${num(b.clicks)} clicks, ${num(b.impressions)} impressions, ` +
    `CTR ${num(b.ctr, 1)}%, avg position ${num(b.position, 1)}, ${num(b.queries)} queries.`
  );
}

const why = (note: string) => note.trim() || '(no note)';

/**
 * What the agent reads when it claims the job, rendered from the "retro-opt" prompt (Agents → Prompts).
 * Numbers included so it never re-derives what we already know.
 */
export function formatRetroOptQueueNote(clientName: string, brief: RetroOptBrief['retro_opt'], template: string = PROMPT_DEFAULTS['retro-opt']): string {
  return renderPrompt(template, {
    client: clientName,
    page_url: brief.url,
    page_id: brief.page_id,
    primary_phrase: brief.primary_phrase || '(pick it from the page’s Search Console queries)',
    why: why(brief.note),
    search_console: searchConsoleLine(brief.baseline),
  });
}

/** "OVER · "ceramic coating" — 68 uses, 3.2% of body words, 17 of 41 H1–H3 · in URL, H1 · pos 14.9 on 175 impr" */
function describeTarget(t: OveroptTarget, headingCount: number | undefined): string {
  if (t.uses == null) return `"${t.phrase}" — typed in by hand, not measured yet: measure it and decide which way it goes`;
  const verdict = t.level === 'over' ? 'OVER' : t.level === 'under' ? 'UNDER' : 'IN LINE';
  const where = [t.in_url && 'URL', t.in_title && 'title', t.in_h1 && 'H1'].filter(Boolean).join(', ');
  return [
    `${verdict}${t.target ? ' (page target)' : ''} · "${t.phrase}" — ${t.uses} use${t.uses === 1 ? '' : 's'}, ${t.density}% of body words, in ${t.headings}${headingCount ? ` of ${headingCount}` : ''} H1–H3`,
    where ? `in ${where}` : null,
    t.position != null ? `pos ${Number(t.position).toFixed(1)} on ${t.impressions ?? 0} impr` : null,
  ]
    .filter(Boolean)
    .join(' · ');
}

export function formatDensityQueueNote(
  clientName: string,
  brief: DensityFixBrief['density_fix'],
  headingCount?: number,
  template: string = PROMPT_DEFAULTS['density-fix'],
): string {
  return renderPrompt(template, {
    client: clientName,
    page_url: brief.url,
    page_id: brief.page_id,
    phrases: brief.phrases.map((t) => `- ${describeTarget(t, headingCount)}`).join('\n'),
    why: why(brief.note),
    search_console: searchConsoleLine(brief.baseline),
  });
}

type QueuePageJobInput = {
  client: { id: string; name: string };
  page: PageRow;
  createdBy: { id: string; name: string };
  title: string;
  queueNote: string;
  brief: RetroOptBrief | DensityFixBrief | TemplateFixBrief | LinksFixBrief;
  /** The shape of the work; omit for General Fulfillment. */
  taskType?: AiJob['task_type'];
  /** One of AI_JOB_MODELS ids; omit / null = agent default. */
  model?: string | null;
  entry: Pick<Database['public']['Tables']['page_optimizations']['Insert'], 'kind' | 'primary_phrase' | 'note' | 'baseline' | 'targets'>;
};

/**
 * Queue one page job and write its journal row.
 *
 * Same shape as an ideation diagnosis: a task to hang the job on, the job, then the row that points at it.
 * The row is read back because an RLS-blocked insert reports no error.
 */
async function queuePageJob({ client, page, createdBy, title, queueNote, brief, taskType, model, entry: fields }: QueuePageJobInput): Promise<{ job: AiJob; entry: PageOptimizationRow }> {
  const { data: task, error: taskError } = await supabase
    .from('client_tasks')
    .insert({
      client_id: client.id,
      title,
      description: queueNote,
      status: 'In Progress',
      due_date: noonTodayIso(),
      assigned_by: createdBy.id,
      assigned_to: createdBy.id,
      assigned_to_name: createdBy.name,
    })
    .select('id')
    .single();
  if (taskError) throw taskError;

  const job = await enqueueAiJob({
    task_id: task.id,
    title,
    card_id: null,
    created_by: createdBy.id,
    queue_note: queueNote,
    kind: 'fulfillment',
    task_type: taskType ?? 'general',
    brief,
    model: model ?? null,
  });

  const { data: entry, error: entryError } = await supabase
    .from('page_optimizations')
    .insert({
      client_id: client.id,
      page_id: page.id,
      job_id: job.id,
      url: page.url,
      created_by: createdBy.id,
      ...fields,
    })
    .select('*')
    .single();
  if (entryError || !entry) {
    throw new Error(`Job ${job.id} is queued, but its journal entry wasn't saved: ${entryError?.message ?? 'no row came back'}`);
  }
  return { job, entry };
}

type Json = Database['public']['Tables']['page_optimizations']['Insert']['baseline'];

export async function enqueueRetroOpt(input: EnqueueRetroOptInput): Promise<{ job: AiJob; entry: PageOptimizationRow }> {
  const { client, page } = input;
  const retro: RetroOptBrief['retro_opt'] = {
    page_id: page.id,
    url: page.url,
    primary_phrase: input.primaryPhrase.trim() || null,
    note: input.note.trim(),
    baseline: baselineOf(page),
  };
  return queuePageJob({
    ...input,
    title: `Retroactive Optimization — ${client.name} ${pathOf(page.url)}`,
    queueNote: formatRetroOptQueueNote(client.name, retro, await loadPromptTemplate('retro-opt')),
    brief: { retro_opt: retro },
    entry: { kind: 'retro_opt', primary_phrase: retro.primary_phrase, note: retro.note, baseline: retro.baseline as unknown as Json },
  });
}

export type EnqueueDensityFixInput = {
  client: { id: string; name: string };
  page: PageRow;
  phrases: OveroptTarget[];
  note: string;
  createdBy: { id: string; name: string };
};

export async function enqueueDensityFix(input: EnqueueDensityFixInput): Promise<{ job: AiJob; entry: PageOptimizationRow }> {
  const { client, page } = input;
  if (!input.phrases.length) throw new Error('Pick at least one phrase.');
  const fix: DensityFixBrief['density_fix'] = {
    page_id: page.id,
    url: page.url,
    phrases: input.phrases,
    note: input.note.trim(),
    baseline: baselineOf(page),
  };
  const headingCount = overoptOf(page)?.headings;
  return queuePageJob({
    ...input,
    title: `Keyword Density Fix — ${client.name} ${pathOf(page.url)}`,
    queueNote: formatDensityQueueNote(client.name, fix, headingCount, await loadPromptTemplate('density-fix')),
    brief: { density_fix: fix },
    entry: {
      kind: 'density_fix',
      primary_phrase: fix.phrases[0].phrase,
      note: fix.note,
      baseline: fix.baseline as unknown as Json,
      targets: fix.phrases as unknown as Json,
    },
  });
}

export type TemplateFixBrief = JobBrief & {
  template_fix: {
    page_id: string;
    url: string;
    share: number | null;
    siblings: { url: string; share: number }[];
    note: string;
    baseline: RetroOptBaseline;
  };
};

const pct = (n: number | null) => (n == null ? '—' : `${Math.round(n * 100)}%`);

export function formatTemplateQueueNote(clientName: string, brief: TemplateFixBrief['template_fix'], template: string = PROMPT_DEFAULTS['template-fix']): string {
  return renderPrompt(template, {
    client: clientName,
    page_url: brief.url,
    page_id: brief.page_id,
    share: pct(brief.share),
    siblings: (brief.siblings.length ? brief.siblings.map((s) => `- ${s.url} — ${pct(s.share)}`) : ['- (none over 20% when queued)']).join('\n'),
    why: why(brief.note),
    search_console: searchConsoleLine(brief.baseline),
  });
}

export type EnqueueTemplateFixInput = {
  client: { id: string; name: string };
  page: PageRow;
  note: string;
  createdBy: { id: string; name: string };
  /** Model the job runs on (Templated Content Fix drawer's picker); null = agent default. */
  model?: string | null;
};

export async function enqueueTemplateFix(input: EnqueueTemplateFixInput): Promise<{ job: AiJob; entry: PageOptimizationRow }> {
  const { client, page } = input;
  const t = templateOf(page);
  const fix: TemplateFixBrief['template_fix'] = {
    page_id: page.id,
    url: page.url,
    share: t?.share ?? null,
    siblings: t?.siblings ?? [],
    note: input.note.trim(),
    baseline: baselineOf(page),
  };
  return queuePageJob({
    ...input,
    title: `Templated Content Fix — ${client.name} ${pathOf(page.url)}`,
    queueNote: formatTemplateQueueNote(client.name, fix, await loadPromptTemplate('template-fix')),
    brief: { template_fix: fix },
    // A rewrite of one page, section by section: a Page Build, so the spec lists the sections to edit.
    taskType: 'page_build',
    entry: {
      kind: 'template_fix',
      primary_phrase: null,
      note: fix.note,
      baseline: fix.baseline as unknown as Json,
      targets: fix.siblings as unknown as Json,
    },
  });
}

export async function fetchPageOptimizations(pageId: string): Promise<PageOptimization[]> {
  const { data, error } = await supabase
    .from('page_optimizations')
    .select('*, job:ai_jobs(id, status, title, spec_body, report_body, approval_body, updated_at)')
    .eq('page_id', pageId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data ?? []) as unknown as PageOptimization[];
}

/** The spec holds two docs. Split on the "## Plan" heading; a spec without one is all Analysis. */
export function splitSpec(spec: string): { analysis: string; plan: string } {
  const body = spec ?? '';
  const match = /^#{1,3}\s*Plan\b.*$/im.exec(body);
  if (!match) return { analysis: body.trim(), plan: '' };
  const analysis = body.slice(0, match.index).replace(/^#{1,3}\s*Analysis\b.*$/im, '').trim();
  const plan = body.slice(match.index + match[0].length).trim();
  return { analysis, plan };
}

export interface WindowStats {
  weeks: number;
  impressions: number;
  clicks: number;
  position: number | null;
}

const DAY = 86400000;
const dayMs = (iso: string) => new Date(`${iso.slice(0, 10)}T00:00:00Z`).getTime();

/** Weekly history rows that sit wholly inside [from, to), averaged per week so windows of any length compare. */
export function windowStats(history: PageHistoryRow[], fromMs: number, toMs: number): WindowStats | null {
  const rows = history.filter((h) => {
    const start = dayMs(h.period_start);
    return start >= fromMs && start + 7 * DAY <= toMs;
  });
  if (!rows.length) return null;
  const impressions = rows.reduce((s, r) => s + (r.impressions ?? 0), 0);
  const clicks = rows.reduce((s, r) => s + (r.clicks ?? 0), 0);
  const weighted = rows.reduce((s, r) => s + (r.position ?? 0) * (r.impressions ?? 0), 0);
  return {
    weeks: rows.length,
    impressions: impressions / rows.length,
    clicks: clicks / rows.length,
    position: impressions ? weighted / impressions : null,
  };
}

/**
 * Before / +28 / +56 for one entry, from the page's weekly history. A checkpoint shows only once all of
 * its weeks are in, so a half-measured window never reads as a result.
 */
export function checkpoints(history: PageHistoryRow[], shippedAt: string | null) {
  if (!shippedAt) return { before: null, d28: null, d56: null };
  const ship = dayMs(shippedAt);
  const full = (days: number) => {
    const stats = windowStats(history, ship, ship + days * DAY);
    return stats && stats.weeks >= Math.floor(days / 7) - 1 ? stats : null;
  };
  return { before: windowStats(history, ship - 28 * DAY, ship), d28: full(28), d56: full(56) };
}

export type LinksFixBrief = JobBrief & {
  links_fix: {
    page_id: string;
    url: string;
    /** Internal links pointing at the page when queued (Ahrefs crawl), null when the crawl hadn't counted them. */
    inlinks: number | null;
    note: string;
    baseline: RetroOptBaseline;
  };
};

/** What the agent reads when it claims an Internal Links Fix, rendered from the "links-fix" prompt (Agents → Prompts). */
export function formatLinksQueueNote(clientName: string, brief: LinksFixBrief['links_fix'], template: string = PROMPT_DEFAULTS['links-fix']): string {
  return renderPrompt(template, {
    client: clientName,
    page_url: brief.url,
    page_id: brief.page_id,
    inlinks: brief.inlinks == null ? 'not counted yet' : String(brief.inlinks),
    why: why(brief.note),
    search_console: searchConsoleLine(brief.baseline),
  });
}

export type EnqueueLinksFixInput = {
  client: { id: string; name: string };
  page: PageRow;
  note: string;
  createdBy: { id: string; name: string };
  /** Model the job runs on; null = agent default. */
  model?: string | null;
};

/**
 * Queue an Internal Links Fix: links into this page from 2–5 relevant pages with room under their cap (skill
 * internal-links-fix). General Fulfillment: it edits other pages' copy, one sentence each, never this page's design.
 */
export async function enqueueLinksFix(input: EnqueueLinksFixInput): Promise<{ job: AiJob; entry: PageOptimizationRow }> {
  const { client, page } = input;
  const fix: LinksFixBrief['links_fix'] = {
    page_id: page.id,
    url: page.url,
    inlinks: page.inlinks ?? null,
    note: input.note.trim(),
    baseline: baselineOf(page),
  };
  return queuePageJob({
    ...input,
    title: `Internal Links Fix — ${client.name} ${pathOf(page.url)}`,
    queueNote: formatLinksQueueNote(client.name, fix, await loadPromptTemplate('links-fix')),
    brief: { links_fix: fix },
    entry: {
      kind: 'links_fix',
      primary_phrase: null,
      note: fix.note,
      baseline: fix.baseline as unknown as Json,
      targets: null,
    },
  });
}

/**
 * Record that someone clicked "Request indexing" for this page in Search Console. Google keeps no history of those
 * requests and allows about 10 a day, so this row is how we know not to ask twice. No job; shipped_at is the request
 * time, so the +28 / +56 day numbers measure from it.
 */
export async function logIndexRequest(input: { client: { id: string }; page: PageRow; createdBy: { id: string } }): Promise<PageOptimizationRow> {
  const { client, page, createdBy } = input;
  const { data, error } = await supabase
    .from('page_optimizations')
    .insert({
      client_id: client.id,
      page_id: page.id,
      job_id: null,
      url: page.url,
      kind: 'index_request',
      note: '',
      baseline: baselineOf(page) as unknown as Json,
      shipped_at: new Date().toISOString(),
      created_by: createdBy.id,
    })
    .select('*')
    .single();
  if (error || !data) throw new Error(error?.message ?? 'No row came back.');
  return data;
}
